import { queryOptions } from '@tanstack/react-query';

import type {
  ExternalInviteApplicationResponseDto,
  ExternalInviteValidationDto,
  GetScreenerResponseDto,
  SaveScreenerResponseDto,
  SaveScreenerResponseResponseDto,
} from '@/api/core-api/types.gen';

import {
  externalInvitesControllerValidate,
  externalScreenerControllerGetScreener,
  externalScreenerControllerSaveScreenerResponse,
} from '@/api/core-api';
import { BASE_PATH } from '@/constants/config';
import { getClient } from '@/utils/get-api-client';

/**
 * The landing page's read: `@NoAuth` on core-api, so `getClient()` with no
 * user id authenticates as the app itself. A 404 means no live link.
 */
export const externalInviteQueryOptions = ({
  inviteCode,
  uid,
}: {
  inviteCode: string;
  uid?: string;
}) =>
  queryOptions({
    queryKey: ['external-invite', inviteCode, uid ?? null],
    queryFn: async (): Promise<ExternalInviteValidationDto> => {
      const response = await externalInvitesControllerValidate({
        client: getClient(),
        path: { inviteCode },
        ...(uid ? { query: { uid } } : {}),
      });

      if (!response.data) {
        throw new Error('Invitation not found');
      }

      return response.data;
    },
    retry: false,
  });

/**
 * The signed-in external participant's screener, with their draft for
 * resuming. Bound to the session's user on the server; the browser goes
 * through the cookie-carrying proxy.
 */
export const externalScreenerQueryOptions = ({
  projectId,
  userId,
}: {
  projectId: string;
  userId: string;
}) =>
  queryOptions({
    queryKey: ['external-screener', projectId, userId],
    queryFn: async (): Promise<GetScreenerResponseDto> => {
      const response = await externalScreenerControllerGetScreener({
        client: getClient(userId),
        path: { projectId },
      });

      if (!response.data) {
        throw new Error('Failed to load the screener');
      }

      return response.data;
    },
    retry: false,
  });

/**
 * Save the answers given (normally the one being left) or, with no answers
 * and `agree`, the project agreement. Same contract as the participant app's
 * per-answer save: the response says where the skip logic leads next and,
 * once filed, the application's status.
 */
export async function saveExternalScreenerAnswers(params: {
  projectId: string;
  responses: SaveScreenerResponseDto['responses'];
  totalTime: number;
  agree?: boolean;
}): Promise<SaveScreenerResponseResponseDto> {
  const response = await externalScreenerControllerSaveScreenerResponse({
    path: { projectId: params.projectId },
    body: {
      responses: params.responses,
      totalTime: params.totalTime,
      ...(params.agree !== undefined && { agree: params.agree }),
    },
  });

  if (!response.data) {
    const error: unknown = response.error;
    throw new Error(
      typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof error.message === 'string'
        ? error.message
        : 'Failed to save your answer',
    );
  }

  return response.data;
}

/**
 * Email the applicant the six-digit code the accept step must carry. Through
 * the BFF, which rate limits it like a signup.
 */
export async function sendExternalInviteCode(params: {
  inviteCode: string;
  contactEmail: string;
  captchaToken: string;
  captchaV2Token?: string;
}): Promise<void> {
  const response = await fetch(
    `${BASE_PATH}/api/participants/external-invite-send-code`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
  );

  if (response.ok) {
    return;
  }

  const body: unknown = await response.json().catch(() => null);
  const error =
    typeof body === 'object' && body !== null && 'error' in body
      ? String((body as { error: unknown }).error)
      : 'sendFailed';
  const message =
    typeof body === 'object' && body !== null && 'message' in body
      ? String((body as { message: unknown }).message)
      : 'Could not send the verification code';
  throw new ExternalInviteSubmitError(error, message, response.status);
}

export interface ExternalInviteSubmitInput {
  inviteCode: string;
  contactEmail: string;
  /** The six-digit code emailed by {@link sendExternalInviteCode}. */
  verificationCode: string;
  captchaToken: string;
  captchaV2Token?: string;
  privacyAccepted: true;
  uid?: string;
  /** The researcher's own parameters on the link, besides `uid`. */
  linkParams?: Record<string, string>;
  timezone?: string;
}

export class ExternalInviteSubmitError extends Error {
  public constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ExternalInviteSubmitError';
  }
}

/** The accept step, through the BFF that mints the session afterwards. */
export async function submitExternalInvite(
  input: ExternalInviteSubmitInput,
): Promise<ExternalInviteApplicationResponseDto> {
  const response = await fetch(
    `${BASE_PATH}/api/participants/external-invite-submit`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error =
      typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : 'submitFailed';
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message: unknown }).message)
        : 'Could not submit your application';
    throw new ExternalInviteSubmitError(error, message, response.status);
  }

  return body as ExternalInviteApplicationResponseDto;
}
